import type { PredictionContractListComplexContract } from '@/types/prediction.types';
import {
  classifyIntradayContract,
  formatMeasuredPeriod,
  isIntradayExpiration,
  isNotYetStarted,
} from '@/utils/intradayContracts';
import type { IntradayTiming } from '@/utils/intradayContracts';
import { isMarketClosed } from '@/utils/marketClosed';

/** ForecastEx exchange identifier used across the prediction APIs. */
const FORECASTX = 'FORECASTX';

/** A unique expiration plus the fields used to sort and label it in the Product Details dropdown. */
export interface ExpirationOption {
  /** Expiration date in YYYYMMDD format — the dropdown value. */
  expiration: string;
  /** Short-form measured period (e.g. "Dec17'26"), when provided by the service. */
  measured_period?: string;
  interval_type?: string;
  /** Start of trading in epoch seconds; not sufficient on its own to identify intraday contracts. */
  start_trade_time?: number;
  /** Last trade time in epoch seconds, taken from the group's first venue contract (constant across venues for a given expiration instance). Used to label a visible dropdown option "(closed)". */
  last_trade_time?: number;
}

export interface ExpirationSelectOption {
  label: string;
  value: string;
}

/** Use the service's measured period as the display label; expiration is only a fallback. */
export function expirationOptionLabel(
  option: ExpirationOption,
  formatExpiration: (expiration: string) => string,
): string {
  return formatMeasuredPeriod({
    ...option,
    measured_period: option.measured_period?.trim() || formatExpiration(option.expiration),
  });
}

/** Build display options, falling back to the formatted expiration when no measured period is available. */
export function buildExpirationSelectOptions(
  expirationOptions: ExpirationOption[],
  preferMeasuredPeriod: boolean,
  formatExpiration: (expiration: string) => string,
): ExpirationSelectOption[] {
  return expirationOptions.map((option) => ({
    label: preferMeasuredPeriod ? expirationOptionLabel(option, formatExpiration) : formatExpiration(option.expiration),
    value: option.expiration,
  }));
}

/** Return the four-digit year represented by a contract's measured period. */
export function getMeasuredPeriodYear(measuredPeriod?: string): string {
  const period = measuredPeriod ?? '';
  const fullYear = period.match(/(?:19|20)\d{2}/)?.[0];
  if (fullYear) return fullYear;

  const shortYear = period.match(/['’](\d{2})\b/)?.[1];
  return shortYear ? `20${shortYear}` : period;
}

/**
 * Election grouping key: the measured period's year, falling back to the expiration's year when the
 * service omits `measured_period`. Without the fallback such a product has no selectable year, so
 * every contract is filtered out. Shared by the year options and the contract filter so both agree.
 */
export function electionYearKey(contract: { expiration: string; measured_period?: string }): string {
  return getMeasuredPeriodYear(contract.measured_period?.trim()) || (contract.expiration ?? '').slice(0, 4);
}

/** Build one chronologically ordered selector option per election year. */
export function buildElectionYearOptions(
  complexContracts: PredictionContractListComplexContract[] | undefined,
): Array<{ label: string; value: string }> {
  const years = new Set(complexContracts?.map(electionYearKey).filter(Boolean));
  return [...years].sort().map((year) => ({ label: year, value: year }));
}

/**
 * The chronological instant an option represents, used to order two options whose coarse date
 * key (measured period date, or expiration as a fallback) is identical.
 *
 * This key is date-granular, so every settlement window on one calendar date ties —
 * e.g. the three "2026.7.31" Dogecoin windows (an hourly 1200 CT, an hourly 1500 CT, and
 * same-day untimed windows). Without this, a tie left them in raw payload order, which put a
 * live window above already-closed ones. `last_trade_time` (the settlement instant) is the
 * truest ordering key; `start_trade_time` backs it up for a window that has not been assigned one.
 */
function optionInstant(option: ExpirationOption): number {
  return option.last_trade_time ?? option.start_trade_time ?? 0;
}

function measuredPeriodDate(measuredPeriod?: string): number | undefined {
  const period = measuredPeriod?.trim();
  if (!period) return undefined;

  const numeric = period.match(/^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/);
  const named = period.match(/^([a-z]{3})(\d{1,2})?['’](\d{2})(?:\s+\d{1,4}(?::\d{2})?(?:\s+[ap]m)?\s+[a-z]{2,4})?$/i);
  if (!numeric && !named) return undefined;

  const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const year = numeric ? Number(numeric[1]) : 2000 + Number(named![3]);
  const month = numeric ? Number(numeric[2] ?? 0) : months.indexOf(named![1].toLowerCase()) + 1;
  const day = numeric ? Number(numeric[3] ?? 0) : Number(named![2] ?? 0);
  if (year < 1000 || ((numeric?.[2] || named) && (month < 1 || month > 12))) return undefined;
  if ((numeric?.[3] || named?.[2]) && (day < 1 || day > new Date(Date.UTC(year, month, 0)).getUTCDate())) {
    return undefined;
  }

  return year * 10000 + month * 100 + day;
}

/**
 * True when every contract in the product belongs to the ForecastEx (FORECASTX) exchange,
 * i.e. a FEX product by itself with no other venues grouped in. Returns false for an empty
 * or contract-less product so callers keep the status-quo behavior.
 */
export function isForecastExOnly(complexContracts: PredictionContractListComplexContract[] | undefined): boolean {
  if (!complexContracts?.length) return false;
  let sawContract = false;
  for (const cc of complexContracts) {
    for (const c of cc.contracts) {
      sawContract = true;
      if (c.exchange !== FORECASTX) return false;
    }
  }
  return sawContract;
}

/**
 * Key used to dedup complex_contracts into one ExpirationOption per measured period — one dropdown
 * row per period, with every venue offering that period merged into it.
 *
 * The service-provided measured period is the identity for every contract, including ordinary
 * dated periods. This preserves same-date distinctions such as "Dec31'26 10:00 AM ET" versus
 * "Dec31'26 2:00 PM ET". `start_trade_time` is timing metadata rather than a grouping key because it
 * can differ between strike batches in the same settlement ladder. If the service omits
 * `measured_period`, the expiration date is the fallback identity.
 */
export function expirationOptionKey(cc: IntradayTiming & { expiration: string; measured_period?: string }): string {
  const measuredPeriod = cc.measured_period?.trim();
  return measuredPeriod || cc.expiration;
}

export function getIntradayMeasuredPeriod(opt: IntradayTiming & { measured_period?: string }): string | undefined {
  return isIntradayExpiration(opt) ? opt.measured_period || undefined : undefined;
}

function hasTradingWindow(timing?: {
  start_trade_time?: number;
  last_trade_time?: number;
}): timing is { start_trade_time: number; last_trade_time: number } {
  return (
    timing?.start_trade_time != null &&
    timing.last_trade_time != null &&
    Number.isFinite(timing.start_trade_time) &&
    Number.isFinite(timing.last_trade_time) &&
    timing.start_trade_time > 0 &&
    timing.last_trade_time > timing.start_trade_time
  );
}

function contractTiming(cc: PredictionContractListComplexContract) {
  return cc.contracts.find(hasTradingWindow) ?? cc.contracts[0];
}

/**
 * Build the unique, ordered list of expirations for the Product Details dropdown.
 *
 * Products are sorted chronologically by measured period, falling back to their YYYYMMDD
 * expiration when the period is missing or unrecognized. Year-only and month-only periods
 * precede more specific dates within that period. The first complex
 * contract seen for each distinct settlement window (see expirationOptionKey) supplies
 * the measured period. Merged intraday timing is aggregated independently below.
 *
 * Contracts with the same measured period merge across strikes and venues. A contract without a
 * measured period uses the expiration fallback identity and remains separate from named periods.
 *
 * The chronological sort keys can still tie for same-day windows, so those fall through to
 * `optionInstant` rather than retaining arbitrary payload order.
 */
export function buildExpirationOptions(
  complexContracts: PredictionContractListComplexContract[] | undefined,
): ExpirationOption[] {
  if (!complexContracts?.length) return [];

  const byExpiration = new Map<string, ExpirationOption>();
  for (const cc of complexContracts) {
    const timing = contractTiming(cc);
    const key = expirationOptionKey(cc);
    const existing = byExpiration.get(key);
    if (!existing) {
      byExpiration.set(key, {
        expiration: cc.expiration,
        measured_period: cc.measured_period,
        interval_type: cc.interval_type,
        start_trade_time: timing?.start_trade_time,
        last_trade_time: timing?.last_trade_time,
      });
      continue;
    }
    // Backfill optional metadata across contracts sharing this exact measured-period identity.
    existing.measured_period ??= cc.measured_period;
    // A measured period may arrive in multiple strike batches with different availability times.
    // Aggregate only complete, independently valid intraday windows so partial timing from two
    // ordinary venues can never be combined into an intraday window that neither venue supplied.
    if (isIntradayExpiration(existing) && hasTradingWindow(existing) && hasTradingWindow(timing)) {
      const incomingLastTradeTime = timing.last_trade_time;
      existing.start_trade_time = Math.min(existing.start_trade_time, timing.start_trade_time);
      existing.last_trade_time = Math.max(existing.last_trade_time!, incomingLastTradeTime!);
      continue;
    }
    if (
      (!hasTradingWindow(existing) && hasTradingWindow(timing)) ||
      (existing.last_trade_time == null && timing?.last_trade_time != null)
    ) {
      existing.start_trade_time = timing.start_trade_time;
      existing.last_trade_time = timing.last_trade_time;
    }
  }

  const options = [...byExpiration.values()];
  options.sort((a, b) => {
    const dateA = measuredPeriodDate(a.measured_period) ?? Number(a.expiration);
    const dateB = measuredPeriodDate(b.measured_period) ?? Number(b.expiration);
    if (dateA !== dateB) return dateA - dateB;
    return optionInstant(a) - optionInstant(b);
  });
  return options;
}

/** Past windows kept alongside the current one (H) on an intraday product, i.e. just H-1. */
const INTRADAY_PAST_WINDOW_COUNT = 1;

/**
 * Reduce a sorted list of expiration options to what the Measured Period dropdown should
 * show (FORECASTUI-2726): the current window (H) plus the `INTRADAY_PAST_WINDOW_COUNT` most
 * recent ones before it (H-1). Not-yet-started windows (H+1 and beyond) are never shown — a
 * future hour only becomes visible at its own `start_trade_time`, when it rolls in as the new H and
 * the outgoing H rolls back to H-1. Filtering is computed globally across the response —
 * contracts are not grouped per candidate/strike.
 *
 * The cap counts every window of the series that has already begun, not just the
 * `start_trade_time`-bearing ones, because the service also returns same-day settlement windows with
 * no `start_trade_time` at all (observed on Dogecoin 851524346: three "Jul31'26" options, two already
 * closed). Those are past windows of the series just as much as a stamped hourly one, so
 * exempting them rendered two H-1 rows. Ordering uses `optionInstant`, so "most recent" is by
 * settlement time rather than payload order.
 *
 * Open longer-dated options (Daily/Weekly/Monthly/Yearly) pass through untouched — a product's
 * future dated expirations are not part of the rolling series and stay listed. On a product with
 * no intraday contracts at all, nothing is filtered: an ordinary product's closed expirations
 * are its own history, not a rolling hourly series.
 *
 * Exception: when nothing in the series has begun *and* the product has no open dated
 * (Daily/Weekly/…) option to fall back on, the earliest upcoming window is kept — otherwise a
 * pre-open intraday-only product would show nothing at all to select or count down to. A product
 * that does have a Daily shows the Daily instead, since that is what is actually tradable.
 */
export function filterToTradableIntradayWindow(options: ExpirationOption[]): ExpirationOption[] {
  const byInterval = new Map<number, ExpirationOption[]>();
  const dated: ExpirationOption[] = [];
  for (const option of options) {
    const classification = classifyIntradayContract(option);
    if (!classification) {
      dated.push(option);
      continue;
    }
    const group = byInterval.get(classification.intervalSeconds) ?? [];
    group.push(option);
    byInterval.set(classification.intervalSeconds, group);
  }
  if (!byInterval.size) return options;

  const visible = new Set(
    [...byInterval.values()].flatMap((group) => filterSingleIntradaySeries([...dated, ...group])),
  );
  return options.filter((option) => visible.has(option));
}

function filterSingleIntradaySeries(options: ExpirationOption[]): ExpirationOption[] {
  const byInstant = (a: ExpirationOption, b: ExpirationOption) => optionInstant(a) - optionInstant(b);
  const isFutureWindow = (o: ExpirationOption) => isNotYetStarted(o.start_trade_time);

  // A dated option still open for trading (the Daily, and any longer-dated expiration) is not
  // part of the intraday series and is always kept — there is always something tradable listed.
  const datedOpen = options.filter((o) => !isIntradayExpiration(o) && !isMarketClosed(o.last_trade_time));
  const datedOpenSet = new Set(datedOpen);

  // Everything in the series that has already begun, oldest first: H-N..H-1 and H, whether H is
  // still open or has just closed ahead of the next window's start.
  const begun = options.filter((o) => !datedOpenSet.has(o) && !isFutureWindow(o)).sort(byInstant);

  const currentAndPast = begun.slice(-(INTRADAY_PAST_WINDOW_COUNT + 1));
  const noFallback = !begun.length && !datedOpen.some((option) => !isFutureWindow(option));
  const upcoming = noFallback
    ? options
        .filter((option) => isIntradayExpiration(option) && isFutureWindow(option))
        .sort(byInstant)
        .slice(0, 1)
    : [];

  // Selected by object identity rather than by expiration or expirationOptionKey: several options
  // can share either (a dated and an intraday option on one date, or two same-day windows the
  // service left untimed), and matching on a shared key would drag in the siblings of every
  // option picked — e.g. keeping one H-1 would keep every untimed window of that date.
  const keep = new Set([...datedOpen, ...currentAndPast, ...upcoming]);
  return options.filter((o) => keep.has(o));
}

/**
 * Resolve the expiration a deep link (`?conid_yes=` / `?conid_no=`) should select, or undefined
 * to fall through to the normal current-window selection.
 *
 * A deep link is honored only while its own window is tradable right now — started and not yet
 * settled. Intraday links are only valid for their own hour, in both directions:
 *
 * - Already settled: a shared "Jul31'26 1200 CT" URL opened at 12:30 targets a window that has
 *   closed, and pinning to it would leave the page on an untradable contract.
 * - Not yet open: a "Jul31'26 1500 CT" URL opened at 12:30 targets an hour the market has not
 *   reached. `filterToTradableIntradayWindow` deliberately hides upcoming windows, so honoring it
 *   would also select a key absent from the dropdown.
 *
 * Returning undefined in either case lets the caller select the live hour, else the Daily.
 */
export function findDeepLinkExpiration(
  complexContracts: PredictionContractListComplexContract[] | undefined,
  conid: number,
  isNoSide: boolean,
  currentOption?: ExpirationOption,
): PredictionContractListComplexContract | undefined {
  if (!conid || !complexContracts?.length) return undefined;
  const match = complexContracts.find((cc) =>
    cc.contracts.some((c) => (isNoSide ? c.conid_no === conid : c.conid_yes === conid)),
  );
  if (!match) return undefined;
  const venue = match.contracts.find((contract) => (isNoSide ? contract.conid_no : contract.conid_yes) === conid)!;
  if (isNotYetStarted(venue.start_trade_time) || isMarketClosed(venue.last_trade_time)) return undefined;
  // Product/card links commonly point at the Daily contract. On a product with a live
  // intraday window, the Daily is only the between-window fallback and must not pin refreshes
  // away from the current hour. A link to a live intraday contract remains authoritative.
  if (!isIntradayExpiration(match) && currentOption && isIntradayExpiration(currentOption)) return undefined;
  return match;
}

/**
 * Pick the option that should be treated as current — the thing to roll selection into, and
 * always something actually tradable.
 *
 * A window only becomes current once its own `start_trade_time` has passed: a not-yet-started window is
 * never returned while anything tradable exists, so selection never rolls ahead of the market.
 * Preference order:
 *
 * 1. The most recently started intraday window still open for trading (the live hour, H).
 * 2. An open dated option — the Daily, else the nearest-dated one. This covers the gap between
 *    one hour's close and the next hour's `start_trade_time`, and the end of the intraday day: there is
 *    always something available to trade rather than a closed window.
 * 3. The most recently started window even though closed, when the product offers nothing open —
 *    better than pointing at a future window the dropdown deliberately hides.
 * 4. The earliest upcoming window, only for a product whose series has not opened at all and that
 *    has no dated option. Mirrors the pre-open exception in `filterToTradableIntradayWindow`.
 */
export function findCurrentIntradayOption(
  options: ExpirationOption[],
  preferredIntervalSeconds?: number,
): ExpirationOption | undefined {
  const byStart = (a: ExpirationOption, b: ExpirationOption) => (a.start_trade_time ?? 0) - (b.start_trade_time ?? 0);
  const isOpen = (option: ExpirationOption) => !isMarketClosed(option.last_trade_time);

  const intraday = options.filter((option) => {
    const classification = classifyIntradayContract(option);
    return (
      classification &&
      (preferredIntervalSeconds === undefined || classification.intervalSeconds === preferredIntervalSeconds)
    );
  });
  const started = intraday.filter((option) => !isNotYetStarted(option.start_trade_time)).sort(byStart);

  const openStarted = started.filter(isOpen);
  if (openStarted.length) return openStarted[openStarted.length - 1];

  // Nothing intraday is live — roll into whatever dated contract is tradable (the Daily).
  const datedOpen = options
    .filter((option) => !isIntradayExpiration(option) && isOpen(option) && !isNotYetStarted(option.start_trade_time))
    .sort((a, b) => optionInstant(a) - optionInstant(b));
  if (datedOpen.length) return datedOpen[0];

  if (started.length) return started[started.length - 1];

  return intraday.filter((option) => isNotYetStarted(option.start_trade_time)).sort(byStart)[0];
}
